import * as Yup from "yup";
import { verifyIranianNationalId } from "@persian-tools/persian-tools";

import { bulkSMSTypeItems, userRoleItems } from "../enum";

// Link
const linkRegex =
  /^(?!.*(?:https?:\/\/|www\.|(?:\b[a-zA-Z0-9\u0600-\u06FF-]+(?:\.[a-zA-Z\u0600-\u06FF]{2,})+\b))).*$/s;

// Phone Number
const phoneNumberRegex = /^09\d{9}$/;

export const isValidPhoneNumber = (phoneNumber) => {
  return phoneNumberRegex.test(phoneNumber);
};

// Email
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (email) => {
  return emailRegex.test(email);
};

// Text
const persianRegex = /^[\u0600-\u06FF\u200C\u200B\s]+$/;
const englishRegex = /^[A-Za-z\s]+$/;

// Date
export const isDateBiggerThan = (firstDate, secondDate) => {
  if (secondDate) {
    const convertFirstDate = new Date(firstDate);
    const convertSecondDate = new Date(secondDate);

    return convertSecondDate.getTime() < convertFirstDate.getTime();
  } else {
    const dateNow = new Date();
    const convertFirstDate = new Date(firstDate);

    return dateNow.getTime() < convertFirstDate.getTime();
  }
};

// Validation
const phoneNumber = Yup.string()
  .required("شماره موبایل الزامی است")
  .min(11, "شماره موبایل نمی تواند کمتر از 11 کاراکتر باشد")
  .max(11, "شماره موبایل نمی تواند بیشتر از 11 کاراکتر باشد")
  .matches(phoneNumberRegex, "شماره موبایل وارد شده معتبر نیست");
const password = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value))
  .min(6, "کلمه عبور نمی تواند کمتر از 6 کاراکتر باشد")
  .max(255, "کلمه عبور نمی تواند بیشتر از 255 کاراکتر باشد");
const newPassword = Yup.string()
  .required("کلمه عبور جدید الزامی است")
  .min(6, "کلمه عبور جدید نمی تواند کمتر از 6 کاراکتر باشد")
  .max(255, "کلمه عبور جدید نمی تواند بیشتر از 255 کاراکتر باشد");
const confirmPassword = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value))
  .oneOf(
    [Yup.ref("password"), null],
    "کلمه عبور و تکرار کلمه عبور یکسان نیستند",
  );
const firstName = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value))
  .max(255, "نام نمی تواند بیشتر از 255 کاراکتر باشد")
  .test("is-persian", "نام باید حروف فارسی باشد", (value) => {
    if (!value) return true;
    return /^[\u0600-\u06FF\u200C\u200B\s]+$/.test(value);
  })
  .trim();
const lastName = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value))
  .max(255, "نام نمی تواند بیشتر از 255 کاراکتر باشد")
  .test("is-persian", "نام باید حروف فارسی باشد", (value) => {
    if (!value) return true;
    return /^[\u0600-\u06FF\u200C\u200B\s]+$/.test(value);
  })
  .trim();
const email = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value))
  .email("پست الکترونیک وارد شده معتبر نیست")
  .trim();
const nationalNumber = Yup.string()
  .nullable()
  .transform((value, originalValue) => (originalValue === "" ? null : value));
const avatar = Yup.string()
  .max(255, "نام فایل تصویر پروفایل نمی تواند بیشتر از 255 کاراکتر باشد")
  .nullable();
const role = Yup.string()
  .required("نقش الزامی است")
  .oneOf(userRoleItems, "نقش مجاز نیست");
const verifyCode = Yup.string()
  .required("کد تایید الزامی است")
  .min(6, "کد تایید نمی تواند کمتر از 6 کاراکتر باشد")
  .max(6, "کد تایید نمی تواند بیشتر از 6 کاراکتر باشد");
const description = Yup.string().max(
  4096,
  "توضیحات نمی تواند بیشتر از 4096 کاراکتر باشد",
);
const address = Yup.string()
  .required("آدرس الزامی است")
  .max(1024, "توضیحات نمی تواند بیشتر از 1024 کاراکتر باشد");
const title = Yup.string()
  .required("عنوان الزامی است")
  .max(255, "عنوان نمی تواند بیشتر از 255 کاراکتر باشد");
const otp = Yup.string()
  .required("کداعتبارسنجی الزامی است")
  .min(5, "کداعتبارسنجی نمی تواند کمتر از 5 کاراکتر باشد")
  .max(5, "کداعتبارسنجی نمی تواند بیشتر از 5 کاراکتر باشد");
const id = Yup.string().required("شناسه الزامی است");
const user = Yup.object({
  value: id,
  label: id,
}).required("کاربر الزامی است");
const question = Yup.string()
  .required("سوال الزامی است")
  .max(1024, "سوال نمی تواند بیشتر از 1024 کاراکتر باشد");
const answer = Yup.string()
  .required("پاسخ الزامی است")
  .max(4096, "پاسخ نمی تواند بیشتر از 4096 کاراکتر باشد");
const questions = Yup.array(Yup.object({ question, answer }))
  .required("سوالات الزامی است")
  .min(1, "حداقل یک سوال الزامی است");
const bulkMessage = Yup.string()
  .required("پیامک الزامی است")
  .max(512, "پیامک نمی‌تواند بیشتر از 512 کاراکتر باشد")
  .matches(linkRegex, "پیامک نمی‌تواند شامل لینک باشد")
  .trim();
const bulkSMSType = Yup.string()
  .oneOf(bulkSMSTypeItems, "نوع مجاز نیست")
  .required("نوع الزامی است");
const users = Yup.array(user).nullable();

// Login User
export const loginUserValidation = Yup.object({
  nationalNumber,
  password,
});

// Register User
export const registerUserValidation = Yup.object({
  firstName,
  lastName,
  nationalNumber,
  phoneNumber,
  email,
  address,
  password,
  confirmPassword,
});

// Create User
export const confirmRegisterValidation = Yup.object({
  verifyCode,
});

// Forgot Password
export const forgotPasswordValidation = Yup.object({
  nationalNumber,
  phoneNumber,
});

// Edit User
export const editUserValidation = Yup.object({
  avatar: avatar.nullable(),
  firstName,
  lastName,
  phoneNumber,
  email,
  address,
});

// Change Password
export const changePasswordValidation = Yup.object({
  password,
  newPassword,
  confirmPassword: Yup.string()
    .required("تکرار کلمه عبور جدید الزامی است")
    .oneOf(
      [Yup.ref("newPassword"), null],
      "کلمه عبور جدید و تکرار کلمه عبور جدید یکسان نیستند",
    ),
});

// Create User For Admin
export const createUserForAdminValidation = Yup.object().shape({
  firstName,
  lastName,
  nationalNumber,
  phoneNumber,
  email,
  address,
  role,
  password,
  confirmPassword,
});

// Edit User For Admin
export const editUserForAdminValidation = Yup.object().shape({
  firstName,
  lastName,
  email,
  address,
  role,
  nationalNumber,
  phoneNumber,
});

// Change User Password
export const changeUserPasswordValidation = Yup.object().shape({
  password,
  confirmPassword,
});

// Send Phone Number OTP
export const sendPhoneNumberOTPValidation = Yup.object({
  phoneNumber,
});

// Verify Phone Number OTP
export const verifyPhoneNumberOTPValidation = Yup.object({
  phoneNumber,
  otp,
});

// Send Email OTP
export const sendEmailOTPValidation = Yup.object({
  email,
});

// Verify Email OTP
export const verifyEmailOTPValidation = Yup.object({
  email,
  otp,
});

// Create FAQ
export const createFAQValidation = Yup.object().shape({
  title,
  questions,
});

// Send Bulk SMS
export const createBulkSMSValidation = Yup.object().shape({
  title,
  message: bulkMessage,
  type: bulkSMSType,
  description,
  users,
});

// Edit Bulk SMS
export const editBulkSMSValidation = Yup.object().shape({
  title,
  description,
});

// Create Order
export const createOrderValidation = Yup.object().shape({
  courier: user,
  customer: user,
});
